import {
    Aperture,
    ArrowsHorizontal,
    ArrowSquareOut,
    ArrowsVertical,
    Boat,
    BookmarkSimple,
    CalendarBlank,
    Camera,
    CircleHalf,
    Compass,
    IdentificationBadge,
    ImageIcon,
    Percent,
    Phone,
    Speedometer,
} from '@phosphor-icons/react';
import { Button, Copy, EventDetail, formatUtc, VText } from '@vake/ui-kit';
import { Feature } from 'geojson';
import { FC, useEffect, useState } from 'react';
import { useSendToMattermost } from '../../../requests/reports';
import { MatchingLabel, ObjectClass } from '../../../shared/api-enums';
import { DisplayImage } from '../../../shared/components/Popup';
import { formatLatLonDMText, formatLatLonText } from '../../../utils/date';
import { EOBrowserService, getEOBrowserUrl } from '../../../utils/eobrowser';
import { mapTags } from '../../../utils/map-tags';
import { mmsiToFlag, mmsiToFullCountryName } from '../../../utils/mmsi-to-flag';
import { externalVesselUrl } from '../../../utils/mmsiExternalCall';
import {
    darkLabel,
    darkVesselIcon,
    notAbleToVerifyLabel,
    notAbleToVerifyVesselIcon,
    platformIcon,
    verifiedLabel,
    verifiedVesselIcon,
    windmillIcon,
} from '../interactiveReport/reportInfo/legend-data';
import { ManuallyValidate } from './ManuallyValidate';
import { SendToMattermost } from './send-to-mattermost';

type Props = {
    selectedFeature: Feature | null;
    setSelectedFeature: (value: Feature | null) => void;
    bookmarkedInsights: string[];
    setBookmarkedInsights: (uuid: string, insert: boolean) => void;
};
export const VesselInfo2: FC<Props> = ({
    selectedFeature,
    setSelectedFeature,
    bookmarkedInsights,
    setBookmarkedInsights,
}) => {
    const { mutate } = useSendToMattermost();
    const date = selectedFeature!.properties!.timestamp.slice(0, 10);
    const getUrl = (service: EOBrowserService) => {
        return getEOBrowserUrl({
            date: date,
            lat: selectedFeature!.properties!.lat,
            lng: selectedFeature!.properties!.lon,
            imageSource: selectedFeature!.properties!.image_source,
            service: service,
            zoom: 15,
        });
    };
    const matchingLabel = selectedFeature?.properties?.matching_label;
    const objectClass = selectedFeature?.properties?.object_class;
    let matchingInformation = null;
    const vesselInfoFromAIS = {
        title: 'Vessel Info from AIS',
        items: [
            {
                key: 'IMO',
                value: selectedFeature?.properties?.imo || '--',
                icon: <IdentificationBadge size={16} />,
                copy: true,
            },
            {
                key: 'MMSI',
                value: selectedFeature?.properties?.mmsi,
                icon: <Phone size={16} />,
                copy: true,
            },
            {
                key: 'Flag',
                value: mmsiToFullCountryName(selectedFeature?.properties?.mmsi),
                icon: mmsiToFlag(selectedFeature?.properties?.mmsi, 16),
                copy: true,
            },
            {
                key: 'Type',
                value: selectedFeature?.properties?.ship_type || '--',
                icon: <Boat size={16} />,
            },
            {
                key: 'AIS Length',
                value: `${selectedFeature?.properties?.ais_length || '--'} m`,
                icon: <ArrowsHorizontal size={16} />,
            },
            {
                // TODO add est_width in API, not sent at the moment
                key: 'AIS Width',
                value: `${selectedFeature?.properties?.est_width || '--'} m`,
                icon: <ArrowsVertical size={16} />,
            },
            {
                key: 'AIS Speed',
                value: `${selectedFeature?.properties?.est_speed || '--'} kn`,
                icon: <Speedometer size={16} />,
            },
            {
                key: 'AIS Course',
                value: `${selectedFeature?.properties?.course || '--'} deg`,
                icon: <Compass size={16} />,
            },
        ],
    };
    const estimatedVesselInformation = {
        title: 'Estimated Vessel Information',
        items: [
            {
                key: 'Model Confidence',
                value: `${selectedFeature?.properties?.object_class_score * 100}%`,
                icon: <Percent size={16} />,
            },
            {
                key: 'Predicted Length',
                value: `${selectedFeature?.properties?.length || '--'} m`,
                icon: <ArrowsHorizontal size={16} />,
            },
        ],
    };
    const displayData = [];
    if (objectClass === ObjectClass.WINDMILL) {
        const title = 'Windmill';
        matchingInformation = (
            <div className="flex items-center gap-2 px-3 py-2">
                {windmillIcon}
                <div className="grow">
                    <VText>{title}</VText>
                </div>
                <Copy value={title} size={12} />
            </div>
        );
    } else if (objectClass === ObjectClass.PLATFORM) {
        const title = 'Platform';
        matchingInformation = (
            <div className="flex items-center gap-2 px-3 py-2">
                {platformIcon}
                <div className="grow">
                    <VText>{title}</VText>
                </div>
                <Copy value={title} size={12} />
            </div>
        );
    } else {
        if (matchingLabel === MatchingLabel.MATCHED) {
            const title = selectedFeature?.properties?.name || selectedFeature?.properties?.mmsi || verifiedLabel;
            matchingInformation = (
                <div className="flex items-center gap-2 px-3 py-2">
                    {verifiedVesselIcon}
                    <div className="grow">
                        <VText>{title}</VText>
                    </div>
                    <Copy value={title} size={12} />
                </div>
            );
            displayData.push(vesselInfoFromAIS);
            displayData.push(estimatedVesselInformation);
        } else if (matchingLabel === MatchingLabel.DARK_VESSEL) {
            matchingInformation = (
                <div className="flex items-center gap-2 px-3 py-2">
                    {darkVesselIcon}
                    <VText>{darkLabel}</VText>
                </div>
            );
            displayData.push(estimatedVesselInformation);
        } else if (matchingLabel === MatchingLabel.UNDETECTED) {
            const title =
                selectedFeature?.properties?.name || selectedFeature?.properties?.mmsi || notAbleToVerifyLabel;
            matchingInformation = (
                <div className="flex items-center gap-2 px-3 py-2">
                    {notAbleToVerifyVesselIcon}
                    <div className="grow">
                        <VText>{title}</VText>
                    </div>
                    <Copy value={title} size={12} />
                </div>
            );
            displayData.push(vesselInfoFromAIS);
        }
    }
    displayData.push({
        title: 'About Observation',
        items: [
            {
                key: 'Time',
                value: formatUtc(new Date(selectedFeature?.properties?.timestamp)),
                icon: <CalendarBlank size={16} />,
            },
            {
                key: 'Satellite',
                value: selectedFeature?.properties?.image_source,
                icon: <Aperture size={16} />,
            },
            {
                key: 'Lat, Lon',
                value: formatLatLonText(selectedFeature) || '',
                icon: <CircleHalf size={16} />,
                copy: true,
            },
            {
                key: 'DMS',
                value: formatLatLonDMText(selectedFeature) || '',
                icon: <CircleHalf size={16} className="-rotate-90" />,
                copy: true,
            },
        ],
    });
    const [bookmarked, setBookmarked] = useState<boolean>(false);
    useEffect(() => {
        setBookmarked(bookmarkedInsights.includes(selectedFeature?.properties?.uuid || false));
    }, [bookmarkedInsights, selectedFeature?.properties?.uuid]);
    const header = (
        <div className="flex gap-3">
            <button
                onClick={() => {
                    mutate(`Bookmarked vessel at [link](${window.location.href})`);
                    setBookmarkedInsights(selectedFeature?.properties?.uuid, !bookmarked);
                    setBookmarked(!bookmarked);
                }}
            >
                <BookmarkSimple size={16} color="white" weight={bookmarked ? 'fill' : 'regular'} />
            </button>

            <Copy value={window.location.href} size={16} />
            <button onClick={() => window.open(externalVesselUrl(selectedFeature?.properties?.mmsi), '_blank')}>
                <ArrowSquareOut color="white" size={16} />
            </button>
        </div>
    );
    return (
        <EventDetail
            header={header}
            tags={mapTags(selectedFeature?.properties?.tags)}
            matchingInformation={matchingInformation}
            backAction={() => setSelectedFeature(null)}
            image={
                <DisplayImage
                    imagePath={selectedFeature?.properties?.image_path}
                    imageSource={selectedFeature?.properties?.image_source}
                    lon={selectedFeature?.properties?.lon}
                    lat={selectedFeature?.properties?.lat}
                    timestamp={selectedFeature?.properties?.timestamp}
                    mmsi={selectedFeature?.properties?.mmsi}
                />
            }
            displayData={displayData}
            actionButtons={
                <div className="flex flex-col gap-1 p-2">
                    <Button
                        text="Open in Copernicus"
                        onClick={() => window.open(getUrl('copernicus'), '_blank')}
                        variant="neutral"
                    />
                    {selectedFeature &&
                        selectedFeature.properties &&
                        typeof selectedFeature.properties?.image_path === 'string' &&
                        selectedFeature.properties?.image_source === 'sentinel2' && (
                            <Button
                                variant="neutral"
                                onClick={() => {
                                    if (selectedFeature.properties) {
                                        const url =
                                            window.location.origin +
                                            '/clearview?insight_search_uuid=' +
                                            JSON.stringify([
                                                selectedFeature.properties.image_path
                                                    .split('sentinel2/')[1]
                                                    .split('.png')[0],
                                            ]);
                                        window.open(url, '_blank');
                                    }
                                }}
                                text="Use VAKE ClearView"
                            />
                        )}
                    {selectedFeature && selectedFeature.properties && (
                        <>
                            <SendToMattermost
                                textOnButton="Order Report Image"
                                iconOnButton={<Camera size={16} />}
                                successMessage="Thank you, we've received your order. We'll send you the image soon."
                                textToSend={`I want a report image for insight uuid ${selectedFeature.properties.uuid} with timestamp ${selectedFeature.properties.timestamp}. [Link to insight](${window.location.href})`}
                            />
                            <SendToMattermost
                                textOnButton={'Request High-Res Image'}
                                iconOnButton={<ImageIcon />}
                                textToSend={`I request a high-resolution image for insight uuid ${selectedFeature.properties.uuid} with timestamp ${selectedFeature.properties.timestamp}. [Link to insight](${window.location.href})`}
                                successMessage={'Thank you! We will follow up with you shortly.'}
                                variant="neutral"
                            />
                        </>
                    )}
                    <ManuallyValidate selectedFeature={selectedFeature} date={date} />
                </div>
            }
        />
    );
};
